import { Link } from "@tanstack/react-router";
import { ArrowRight, PoundSterling } from "lucide-react";
import { COST_PAGES } from "@/lib/cost-pages";

interface Props {
  /** Optional: highlight guides matching this service slug first. */
  currentService?: string;
  /** Optional title override. */
  title?: string;
}

/**
 * Cross-link block injected into service / pricing pages. Surfaces our cost
 * guides so high-intent visitors land on the matching commercial page instead
 * of bouncing off generic pricing copy.
 */
export function HelpfulPricingGuides({ currentService, title = "Helpful cost guides" }: Props) {
  const ordered = [...COST_PAGES].sort((a, b) => {
    if (currentService && a.serviceSlug === currentService) return -1;
    if (currentService && b.serviceSlug === currentService) return 1;
    return 0;
  });
  return (
    <section className="py-12 md:py-16 bg-card">
      <div className="mx-auto max-w-5xl px-4">
        <div className="flex items-center gap-2">
          <PoundSterling className="h-4 w-4 text-primary" />
          <h2 className="text-xl md:text-2xl font-bold text-foreground">{title}</h2>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Honest, no-fixed-price guides explaining what actually affects the bill — and how to get a real quote in under a minute.
        </p>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {ordered.map((p) => (
            <Link
              key={p.slug}
              to="/cost/$slug"
              params={{ slug: p.slug }}
              className="group rounded-xl border border-border bg-background p-4 hover:border-primary/50 transition"
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-sm font-bold text-foreground group-hover:text-primary leading-snug">
                  {p.h1}
                </h3>
                <ArrowRight className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
